import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material';
import { CloudUpload, Download, CheckCircle, Error as ErrorIcon, Visibility } from '@mui/icons-material';
import { userImportService } from '../services/userImportService';
import type { UserImportError, UserImportSuccess, UserImportResult } from '../services/userImportService';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { logger } from '../utils/logger';


export const UserImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<UserImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasImported, setHasImported] = useState(false);
  const [successPage, setSuccessPage] = useState(1);
  const [errorPage, setErrorPage] = useState(1);
  const pageSize = 10;
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Force re-read file to avoid browser cache issues
  const getFreshFile = async (originalFile: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const blob = new Blob([arrayBuffer], { type: originalFile.type });
          const freshFile = new File([blob], originalFile.name, {
            type: originalFile.type,
            lastModified: Date.now()
          });
          resolve(freshFile);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsArrayBuffer(originalFile);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const freshFile = await getFreshFile(selectedFile);
      
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
      ];
      
      if (!allowedTypes.includes(freshFile.type) && !freshFile.name.endsWith('.csv')) {
        setError('Chỉ chấp nhận file Excel (.xlsx, .xls) hoặc CSV (.csv)');
        setFile(freshFile);
        setImportResult(null);
        return;
      }

      // Validate file size (10MB limit)
      if (freshFile.size > 10 * 1024 * 1024) {
        setError('Kích thước file không được vượt quá 10MB');
        setFile(freshFile);
        setImportResult(null);
        return;
      }

      setFile(freshFile);
      setError(null);
      setImportResult(null);
      setHasImported(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await userImportService.downloadTemplate();
      showSuccess('Đã tải xuống template thành công!');
    } catch (err) {
      logger.error('Download template error:', err);
      showError('Lỗi khi tải template');
    }
  };

  const handleUpload = async () => {
    if (!file || isUploading || error) return;

    if (hasImported && file) {
      setShowConfirmDialog(true);
      return;
    }

    setIsUploading(true);
    setError(null);
    setImportResult(null);

    try {
      const freshFile = await getFreshFile(file);
      const result = await userImportService.importUsers(freshFile);
      setImportResult(result);
      setHasImported(true);
      // Reset pagination when new import result is received
      setSuccessPage(1);
      setErrorPage(1);
      
      if (result.success) {
        if (result.failedImports === 0) {
          showSuccess(`✅ Import thành công ${result.successfulImports}/${result.totalRows} người dùng`);
        } else {
          showSuccess(`⚠️ Import hoàn thành: ${result.successfulImports}/${result.totalRows} người dùng thành công, ${result.failedImports} lỗi`);
        }
        setFile(null);
        setError(null);
        const fileInputs = document.querySelectorAll('input[type="file"]') as NodeListOf<HTMLInputElement>;
        fileInputs.forEach(input => {
          input.value = '';
        });
      } else {
        const errorMsg = result && typeof result === 'object' && 'message' in result 
          ? (result as UserImportResult).message 
          : 'Lỗi khi import người dùng';
        showError(`❌ ${errorMsg}`);
      }
      
    } catch (err) {
      logger.error('Import error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Lỗi khi import người dùng';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmReimport = () => {
    setShowConfirmDialog(false);
    setHasImported(false);
    handleUpload();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Import</Typography>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Sử dụng chức năng này để import nhiều người dùng cùng lúc từ file Excel hoặc CSV.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadTemplate}
              sx={{ mr: 2 }}
            >
              Tải Template
            </Button>
          </Box>

          <Box sx={{ mb: 3 }}>
            <input
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              id="user-import-file-input"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="user-import-file-input">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUpload />}
                disabled={isUploading}
              >
                Chọn File
              </Button>
            </label>
            {file && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={file.name}
                  onDelete={() => {
                    setFile(null);
                    setError(null);
                    setImportResult(null);
                    setHasImported(false);
                    const fileInput = document.getElementById('user-import-file-input') as HTMLInputElement;
                    if (fileInput) fileInput.value = '';
                  }}
                  sx={{ mr: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Kích thước: {(file.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isUploading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Đang import người dùng...
              </Typography>
            </Box>
          )}

          {file && !isUploading && !error && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={hasImported}
              sx={{ mt: 2 }}
            >
              {hasImported ? 'Đã Import' : 'Import Người Dùng'}
            </Button>
          )}
        </CardContent>
      </Card>

      {importResult && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Kết Quả Import
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">
                Tổng số: {importResult.totalRows} | 
                Thành công: <span style={{ color: 'green' }}>{importResult.successfulImports}</span> | 
                Lỗi: <span style={{ color: 'red' }}>{importResult.failedImports}</span>
              </Typography>
            </Box>

            {importResult.successes.length > 0 && (() => {
              const totalSuccessPages = Math.ceil(importResult.successes.length / pageSize);
              const startIndex = (successPage - 1) * pageSize;
              const endIndex = startIndex + pageSize;
              const paginatedSuccesses = importResult.successes.slice(startIndex, endIndex);
              
              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <CheckCircle sx={{ color: 'green', mr: 1, verticalAlign: 'middle' }} />
                    Import Thành Công ({importResult.successes.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Dòng</TableCell>
                          <TableCell>Mã CMS</TableCell>
                          <TableCell>Họ và Tên</TableCell>
                          <TableCell>Email</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedSuccesses.map((success, index) => (
                          <TableRow key={index}>
                            <TableCell>{success.row}</TableCell>
                            <TableCell>{success.cmsUserId}</TableCell>
                            <TableCell>{success.fullName}</TableCell>
                            <TableCell>{success.email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {totalSuccessPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={totalSuccessPages}
                        page={successPage}
                        onChange={(_, page) => setSuccessPage(page)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
              );
            })()}

            {importResult.errors.length > 0 && (() => {
              const totalErrorPages = Math.ceil(importResult.errors.length / pageSize);
              const startIndex = (errorPage - 1) * pageSize;
              const endIndex = startIndex + pageSize;
              const paginatedErrors = importResult.errors.slice(startIndex, endIndex);
              
              return (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    <ErrorIcon sx={{ color: 'red', mr: 1, verticalAlign: 'middle' }} />
                    Lỗi Import ({importResult.errors.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Dòng</TableCell>
                          <TableCell>Trường</TableCell>
                          <TableCell>Lỗi</TableCell>
                          <TableCell>Giá trị</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedErrors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell>{error.row}</TableCell>
                            <TableCell>{error.field}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="error">
                                {error.error}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {error.value}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {totalErrorPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={totalErrorPages}
                        page={errorPage}
                        onChange={(_, page) => setErrorPage(page)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
              );
            })()}
          </CardContent>
        </Card>
      )}

      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Xác nhận Import lại</DialogTitle>
        <DialogContent>
          <Typography>
            File này đã được import. Bạn có muốn import lại không?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Hủy</Button>
          <Button onClick={handleConfirmReimport} variant="contained" color="primary">
            Import lại
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};

